import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CommandPalette from "@/components/CommandPalette";
import { House, Briefcase, UsersThree, Receipt, ListChecks, Calendar as CalIcon, ChartBar, CurrencyInr, MagnifyingGlass, SignOut, FilmSlate } from "@phosphor-icons/react";

const nav = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/projects", label: "Projects", icon: FilmSlate },
  { to: "/clients", label: "Clients", icon: Briefcase },
  { to: "/freelancers", label: "Freelancers", icon: UsersThree },
  { to: "/ledger", label: "Freelancer Ledger", icon: Receipt },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/calendar", label: "Calendar", icon: CalIcon },
  { to: "/finance", label: "Finance", icon: CurrencyInr },
  { to: "/reports", label: "Reports", icon: ChartBar },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const nav_ = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] grain">
      <aside data-testid="sidebar" className="w-[260px] shrink-0 hairline-r bg-[#0A0A0A] px-4 py-5 flex flex-col relative z-10">
        <div className="flex items-center gap-2 px-2 pb-6">
          <div className="w-8 h-8 rounded-md bg-[#D97706] flex items-center justify-center">
            <FilmSlate size={18} weight="duotone" color="#0A0A0A" />
          </div>
          <div>
            <div className="text-[15px] font-extrabold tracking-tight leading-none">Motographist</div>
            <div className="text-[10px] text-white/40 mt-0.5 tracking-widest uppercase">Ops</div>
          </div>
        </div>

        <button
          data-testid="open-command-palette"
          onClick={() => setPaletteOpen(true)}
          className="mb-6 flex items-center gap-2 text-xs text-white/50 hover:text-white/80 border border-white/10 rounded-md px-3 py-2 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
        >
          <MagnifyingGlass size={14} />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="mono text-[10px] px-1.5 py-0.5 rounded border border-white/10 bg-black/50">⌘K</kbd>
        </button>

        <nav className="flex-1 space-y-0.5">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] transition-colors ${
                  isActive ? "bg-white/[0.06] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <n.icon size={16} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="pt-4 mt-4 border-t border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold">
            {user?.name?.[0] || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{user?.name}</div>
            <div className="text-[10px] text-white/40 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav_("/login"); }}
            className="text-white/40 hover:text-white transition-colors"
            title="Log out"
          >
            <SignOut size={16} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative z-10">
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
